/**
 * Custom hook for countdown timer functionality
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface CountdownState {
  isActive: boolean;
  remainingSeconds: number;
  isReady: boolean;
}

export const useCountdown = (initialSeconds: number = 10) => {
  const [countdowns, setCountdowns] = useState<Record<string, CountdownState>>({});
  const intervalsRef = useRef<Record<string, NodeJS.Timeout>>({});

  const startCountdown = useCallback(
    (action: string) => {
      // Clear any existing countdown for this action
      if (intervalsRef.current[action]) {
        clearInterval(intervalsRef.current[action]);
      }

      // Initialize countdown state
      setCountdowns((prev) => ({
        ...prev,
        [action]: {
          isActive: true,
          remainingSeconds: initialSeconds,
          isReady: false,
        },
      }));

      // Create interval to update every second
      intervalsRef.current[action] = setInterval(() => {
        setCountdowns((prev) => {
          const current = prev[action];
          if (!current) return prev;

          if (current.remainingSeconds > 0) {
            return {
              ...prev,
              [action]: {
                ...current,
                remainingSeconds: current.remainingSeconds - 1,
              },
            };
          } else {
            // Countdown finished - show ready state
            clearInterval(intervalsRef.current[action]);
            
            // Auto-hide after 5 seconds
            setTimeout(() => {
              setCountdowns((p) => {
                const newState = { ...p };
                delete newState[action];
                return newState;
              });
            }, 5000);

            return {
              ...prev,
              [action]: {
                isActive: false,
                remainingSeconds: 0,
                isReady: true,
              },
            };
          }
        });
      }, 1000);
    },
    [initialSeconds]
  );

  const clearCountdown = useCallback((action: string) => {
    if (intervalsRef.current[action]) {
      clearInterval(intervalsRef.current[action]);
      delete intervalsRef.current[action];
    }
    setCountdowns((prev) => {
      const newState = { ...prev };
      delete newState[action];
      return newState;
    });
  }, []);

  const clearAllCountdowns = useCallback(() => {
    Object.keys(intervalsRef.current).forEach((action) => {
      clearInterval(intervalsRef.current[action]);
    });
    intervalsRef.current = {};
    setCountdowns({});
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(intervalsRef.current).forEach(clearInterval);
    };
  }, []);

  return {
    countdowns,
    startCountdown,
    clearCountdown,
    clearAllCountdowns,
  };
};

